/* IssueTickets — /admin/tickets */
import { useState, useMemo } from 'react';
import css from '../AdminPage.module.css';
import {
  useTickets,
  useTicketStats,
  useCreateTicket,
  useUpdateTicket,
} from '../../../hooks/useAdmin';
import { useToast } from '../../../hooks/useToast';
import type { Ticket } from '../../../types/admin';

export default function IssueTickets() {
  const { data: tickets = [], isLoading } = useTickets();
  const { data: stats } = useTicketStats();
  const createTicket = useCreateTicket();
  const updateTicket = useUpdateTicket();
  const { showToast } = useToast();

  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolvingTicket, setResolvingTicket] = useState<Ticket | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [detailTicket, setDetailTicket] = useState<Ticket | null>(null);

  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [notes, setNotes] = useState('');
  const ROWS_PER_PAGE = 25;
  const [ticketPage, setTicketPage] = useState(1);

  /* avg completion time for resolved tickets */
  const avgCompletionTime = useMemo(() => {
    const resolved = tickets.filter((t) => t.status === 'Resolved' && t.created_at && t.updated_at);
    if (resolved.length === 0) return '—';
    const totalMs = resolved.reduce((sum, t) => {
      const start = new Date(t.created_at!).getTime();
      const end = new Date(t.updated_at!).getTime();
      return sum + Math.max(0, end - start);
    }, 0);
    const avgMs = totalMs / resolved.length;
    const hours = avgMs / (1000 * 60 * 60);
    if (hours < 1) return `${Math.round(avgMs / (1000 * 60))}m`;
    if (hours < 48) return `${Math.round(hours)}h`;
    return `${Math.round(hours / 24)}d`;
  }, [tickets]);

  const filtered = useMemo(() => {
    let list = tickets;
    if (statusFilter) list = list.filter((t) => t.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (t) =>
          (t.title || '').toLowerCase().includes(q) ||
          (t.description || '').toLowerCase().includes(q) ||
          (t.submitter || '').toLowerCase().includes(q),
      );
    }
    return list;
  }, [tickets, statusFilter, search]);

  const ticketTotalPages = Math.ceil(filtered.length / ROWS_PER_PAGE);
  const pagedTickets = filtered.slice((ticketPage - 1) * ROWS_PER_PAGE, ticketPage * ROWS_PER_PAGE);

  function openNew() {
    setTitle('');
    setDesc('');
    setNotes('');
    setShowModal(true);
  }

  function openDetail(t: Ticket) {
    setDetailTicket(t);
    setShowDetail(true);
  }

  function openResolve(t: Ticket) {
    setResolvingTicket(t);
    setNotes('');
    setShowResolveModal(true);
  }

  async function handleResolve() {
    if (!resolvingTicket) return;
    try {
      await updateTicket.mutateAsync({
        id: resolvingTicket.id,
        status: 'Resolved',
        resolution_notes: notes,
      });
      showToast('Ticket resolved', 'success');
      setShowResolveModal(false);
    } catch {
      showToast('Failed to resolve ticket', 'error');
    }
  }

  async function save() {
    try {
      await createTicket.mutateAsync({ title, description: desc });
      showToast('Ticket created', 'success');
      setShowModal(false);
    } catch {
      showToast('Failed to save ticket', 'error');
    }
  }

  function badgeClass(s: string) {
    switch (s) {
      case 'Resolved':
        return css.badgeSuccess;
      case 'In Progress':
        return css.badgeWarning;
      default:
        return css.badgeInfo;
    }
  }

  function handleExportCSV() {
    const headers = [
      'ID',
      'Location / Title',
      'Description',
      'Submitter',
      'Status',
      'Resolution Notes',
      'Created',
      'Updated',
    ];
    const rows = filtered.map((t) => [
      t.id,
      `"${(t.title || '').replace(/"/g, '""')}"`,
      `"${(t.description || '').replace(/"/g, '""')}"`,
      t.submitter || '',
      t.status,
      `"${(t.resolution_notes || '').replace(/"/g, '""')}"`,
      t.submissionDate || (t.created_at ? new Date(t.created_at).toLocaleDateString() : ''),
      t.updated_at ? new Date(t.updated_at).toLocaleString() : '',
    ]);
    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `issue-tickets-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (isLoading) {
    return (
      <div className={css.spinner}>
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
      </div>
    );
  }

  return (
    <>
      {/* Stats */}
      {stats && (
        <div className={css.statsGrid}>
          <div className={css.statCard}>
            <span className={css.statValue}>{stats.total}</span>
            <span className={css.statLabel}>Total</span>
          </div>
          <div className={css.statCard}>
            <span className={css.statValue}>{stats.new}</span>
            <span className={css.statLabel}>New</span>
          </div>
          <div className={css.statCard}>
            <span className={css.statValue}>{stats.inProgress}</span>
            <span className={css.statLabel}>In Progress</span>
          </div>
          <div className={css.statCard}>
            <span className={css.statValue}>{stats.resolved}</span>
            <span className={css.statLabel}>Resolved</span>
          </div>
          <div className={css.statCard}>
            <span className={css.statValue}>{avgCompletionTime}</span>
            <span className={css.statLabel}>Avg Completion</span>
          </div>
        </div>
      )}

      <div className={css.section}>
        <div className={css.sectionHeader}>
          <h2 className={css.sectionTitle}>🎫 Issue Tickets</h2>
          <div className={css.toolbar}>
            <div className={css.searchWrapper}>
              <span className={css.searchIcon}>🔍</span>
              <input
                className={css.searchInput}
                placeholder="Search tickets..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <select
              className={css.formSelect}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ maxWidth: 160 }}
            >
              <option value="">All Statuses</option>
              <option value="New Ticket">New Ticket</option>
              <option value="In Progress">In Progress</option>
              <option value="Resolved">Resolved</option>
            </select>
            <button
              className={css.btnSecondary}
              onClick={handleExportCSV}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}
            >
              📥 Export CSV
            </button>
            <button className={css.btnPrimary} onClick={openNew} style={{ whiteSpace: 'nowrap' }}>
              + New Ticket
            </button>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className={css.emptyState}>
            <div className={css.emptyIcon}>🎫</div>
            <p className={css.emptyMessage}>No tickets found</p>
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table className={css.table}>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Location / Title</th>
                    <th>Description</th>
                    <th>Submitter</th>
                    <th>Status</th>
                    <th>Resolution</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedTickets.map((t) => (
                    <tr
                      key={t.id}
                      onClick={() => openDetail(t)}
                      style={{ cursor: 'pointer' }}
                      title="Click to view details"
                    >
                      <td style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-xs)' }}>
                        {t.id}
                      </td>
                      <td style={{ fontWeight: 600 }}>{t.title}</td>
                      <td
                        style={{
                          maxWidth: 250,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {t.description}
                      </td>
                      <td>{t.submitter || '—'}</td>
                      <td>
                        <span className={badgeClass(t.status)}>{t.status}</span>
                      </td>
                      <td
                        style={{
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: 'var(--font-size-xs)',
                          color: t.resolution_notes ? 'inherit' : 'var(--gray-500)',
                        }}
                      >
                        {t.resolution_notes || '—'}
                      </td>
                      <td style={{ fontSize: 'var(--font-size-xs)', whiteSpace: 'nowrap' }}>
                        {t.submissionDate ||
                          (t.created_at ? new Date(t.created_at).toLocaleDateString() : '—')}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {t.status !== 'Resolved' && (
                          <button
                            className={`${css.btnPrimary} ${css.btnSmall}`}
                            onClick={() => openResolve(t)}
                          >
                            Resolve
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {ticketTotalPages > 1 && (
              <div className={css.pagination}>
                <button
                  className={css.pageBtn}
                  disabled={ticketPage === 1}
                  onClick={() => setTicketPage(ticketPage - 1)}
                >
                  ‹ Prev
                </button>
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-500)' }}>
                  Page {ticketPage} of {ticketTotalPages} ({filtered.length} total)
                </span>
                <button
                  className={css.pageBtn}
                  disabled={ticketPage >= ticketTotalPages}
                  onClick={() => setTicketPage(ticketPage + 1)}
                >
                  Next ›
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create modal */}
      {showModal && (
        <div className={css.modalBackdrop} onClick={() => setShowModal(false)}>
          <div className={css.modal} onClick={(e) => e.stopPropagation()}>
            <div className={css.modalHeader}>
              <h3 className={css.modalTitle}>New Ticket</h3>
              <button className={css.modalClose} onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>
            <div className={css.modalBody}>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Location / Title</label>
                <input
                  className={css.formInput}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Issue Description</label>
                <textarea
                  className={css.formTextarea}
                  value={desc}
                  onChange={(e) => setDesc(e.target.value)}
                  rows={4}
                />
              </div>
            </div>
            <div className={css.modalFooter}>
              <button className={css.btnSecondary} onClick={() => setShowModal(false)}>
                Cancel
              </button>
              <button className={css.btnPrimary} onClick={save} disabled={!title}>
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail view modal (read-only) */}
      {showDetail && detailTicket && (
        <div className={css.modalBackdrop} onClick={() => setShowDetail(false)}>
          <div className={css.modal} onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className={css.modalHeader}>
              <h3 className={css.modalTitle}>Ticket: {detailTicket.id}</h3>
              <button className={css.modalClose} onClick={() => setShowDetail(false)}>
                ×
              </button>
            </div>
            <div className={css.modalBody}>
              <div style={{ display: 'grid', gap: 12 }}>
                <div>
                  <div
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--gray-400)',
                      marginBottom: 2,
                    }}
                  >
                    Location / Title
                  </div>
                  <div style={{ fontWeight: 600 }}>{detailTicket.title}</div>
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--gray-400)',
                      marginBottom: 2,
                    }}
                  >
                    Description
                  </div>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{detailTicket.description || '—'}</div>
                </div>
                <div className={css.grid2}>
                  <div>
                    <div
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--gray-400)',
                        marginBottom: 2,
                      }}
                    >
                      Submitter
                    </div>
                    <div>{detailTicket.submitter || '—'}</div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--gray-400)',
                        marginBottom: 2,
                      }}
                    >
                      Status
                    </div>
                    <span className={badgeClass(detailTicket.status)}>{detailTicket.status}</span>
                  </div>
                </div>
                <div className={css.grid2}>
                  <div>
                    <div
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--gray-400)',
                        marginBottom: 2,
                      }}
                    >
                      Created
                    </div>
                    <div>
                      {detailTicket.submissionDate ||
                        (detailTicket.created_at
                          ? new Date(detailTicket.created_at).toLocaleString()
                          : '—')}
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--gray-400)',
                        marginBottom: 2,
                      }}
                    >
                      Updated
                    </div>
                    <div>
                      {detailTicket.updated_at
                        ? new Date(detailTicket.updated_at).toLocaleString()
                        : '—'}
                    </div>
                  </div>
                </div>
                {detailTicket.resolution_notes && (
                  <div>
                    <div
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--gray-400)',
                        marginBottom: 2,
                      }}
                    >
                      Resolution Notes
                    </div>
                    <div
                      style={{
                        whiteSpace: 'pre-wrap',
                        background: 'var(--gray-50, #f8fafc)',
                        padding: 8,
                        borderRadius: 'var(--radius-sm)',
                      }}
                    >
                      {detailTicket.resolution_notes}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className={css.modalFooter}>
              {detailTicket.status !== 'Resolved' && (
                <button
                  className={css.btnPrimary}
                  onClick={() => {
                    setShowDetail(false);
                    openResolve(detailTicket);
                  }}
                >
                  Resolve
                </button>
              )}
              <button className={css.btnSecondary} onClick={() => setShowDetail(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resolve modal */}
      {showResolveModal && resolvingTicket && (
        <div className={css.modalBackdrop} onClick={() => setShowResolveModal(false)}>
          <div className={css.modal} onClick={(e) => e.stopPropagation()}>
            <div className={css.modalHeader}>
              <h3 className={css.modalTitle}>Resolve Ticket: {resolvingTicket.id}</h3>
              <button className={css.modalClose} onClick={() => setShowResolveModal(false)}>
                ×
              </button>
            </div>
            <div className={css.modalBody}>
              <p
                style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--gray-600)',
                  marginBottom: 12,
                }}
              >
                <strong>Location:</strong> {resolvingTicket.title}
                <br />
                <strong>Issue:</strong> {resolvingTicket.description}
              </p>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Resolution Notes</label>
                <textarea
                  className={css.formTextarea}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                  placeholder="Describe how the issue was resolved..."
                />
              </div>
            </div>
            <div className={css.modalFooter}>
              <button className={css.btnSecondary} onClick={() => setShowResolveModal(false)}>
                Cancel
              </button>
              <button
                className={css.btnPrimary}
                onClick={handleResolve}
                disabled={updateTicket.isPending}
              >
                {updateTicket.isPending ? 'Resolving...' : 'Mark as Resolved'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
